import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { normalizeForecastingResponse } from '@/pages/Forecasting/utils/normalizeForecastingRow';

export interface ForecastingRow {
  Campaign_ID: string;
  Line_Item_ID: string;
  Booking_Date: string | null;
  Status: string;
  Buying_Point: string;
  Advertiser: string;
  Campaign: string;
  Channel: string;
  Platform: string;
  Month: string;
  Year: string;
  Start_Date: string;
  End_Date: string;
  Office_Net_Revenue: string;
  Skyrise_Powered: string;
  Recognised_Revenue__: string;
  Margin__: string;
  Net_Rev__Rec_: string;
  Revenue_Weighting?: string;
  Platform_Spend: string;
  Hard_Costs: string;
  Telco_Costs: string;
  Rebate: string;
  Referral: string;
  Gross_Margin: string;
  Gross_Profit: string;
  Quarter: string;
  Customer_Type: string;
  Month__Date_: string;
  Sales: string;
  AM: string;
  Forecast_Stage: string;
  Simplified_Stages: string;
  Skyrise_Type: string;
  Skyrise_Powered__: string;
  Source: string;
  FY: string;
  New_Existing: string;
  Territory: string;
}

export interface ForecastingQuery {
  year?: string;
  quarter?: string;
  channel?: string;
  status?: string;
  territory?: string;
  fy?: string;
  limit?: number;
  offset?: number;
}

export const forecastingApi = createApi({
  reducerPath: 'forecastingApi',
  baseQuery,
  endpoints: (builder) => ({
    getForecasting: builder.query<ForecastingRow[], ForecastingQuery>({
      query: (params) => {
        const search = new URLSearchParams();
        if (params.year)      search.set('year', params.year);
        if (params.quarter)   search.set('quarter', params.quarter);
        if (params.channel)   search.set('channel', params.channel);
        if (params.status)    search.set('status', params.status);
        if (params.territory) search.set('territory', params.territory);
        if (params.fy)        search.set('fy', params.fy);
        if (params.limit)     search.set('limit', String(params.limit));
        if (params.offset)    search.set('offset', String(params.offset));
        const qs = search.toString();
        return `forecasting/api${qs ? `?${qs}` : ''}`;
      },
      transformResponse: (response: unknown) => normalizeForecastingResponse(response)
    }),
    /* Several financial years as one set of rows, for a date range that spans
       them. Each FY is its own request, in parallel, so a year is the same
       query whether it is fetched alone or beside others. */
    getForecastingForFys: builder.query<ForecastingRow[], { fys: string[] }>({
      async queryFn({ fys }, _api, _extraOptions, fetchWithBQ) {
        const results = await Promise.all(
          fys.map((fy) => fetchWithBQ(`forecasting/api?${new URLSearchParams({ fy })}`))
        );
        const failed = results.find((r) => r.error);
        if (failed?.error) return { error: failed.error };
        return { data: results.flatMap((r) => normalizeForecastingResponse(r.data)) };
      }
    })
  })
});

export const { useGetForecastingQuery, useGetForecastingForFysQuery } = forecastingApi;
